import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import useRerender from '../../Common/components/hooks/useRerender';
import ListPickerModal from '../../Common/components/ListPickerModal';
import { createColumnHelper } from '../../Common/components/Table';
import ActivationActions from './ActivationActions';
import ActivationTagAssociations from './ActivationTagAssociations';
import useTags from './hooks/useTags';
import { Button } from '@/src/shadComponents/ui/button';
import PageWithTable from '@/src/Common/components/PageWithTable';
import { Plus, Filter, Search, Loader2, X } from 'lucide-react';
import ActivationsTable from './ActivationsTable';
import { Input } from '@/src/shadComponents/ui/input';
import { Checkbox } from '@/src/shadComponents/ui/checkbox';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/src/shadComponents/ui/popover';
import { Badge } from '@/src/shadComponents/ui/badge';
import CloneActivationModal from './CloneActivationModal';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import CreateActivationModal from './CreateActivationModal';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

const columnHelper = createColumnHelper<Activation>();

export default function Activations() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigation = useNavigate();
    const [tags, setTags] = useState([]);
    const { getTags } = useTags();
    const [activations, setActivations] = useState<Activation[]>([]);
    const authenticatedApi = useAuthenticatedApi();
    const [showTagFilters, setShowTagFilters] = useState(false);
    const [tagFilters, setTagFilters] = useState<number[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [searchTerms, setSearchTerms] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showingCloneModal, setShowingCloneModal] = useState(false);
    const [activationToClone, setActivationToClone] =
        useState<Activation | null>(null);
    const [isShowingNewActivationModal, setIsShowingNewActivationModal] =
        useState(false);
    const { orgId } = useUserOrg();

    const { render, renderCount } = useRerender();

    const onDelete = () => {
        render();
    };

    const onClone = async (id: number, activation: Activation) => {
        setActivationToClone(activation);
        setShowingCloneModal(true);
    };

    const columnDefinitions = [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => <span>id</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: () => <span>name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('date', {
            id: 'date',
            header: () => <span>date</span>,
            cell: (info) => <span>{info.getValue().split('T')[0]}</span>,
        }),
        columnHelper.accessor('location', {
            id: 'location',
            header: () => <span>location</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.display({
            id: 'tags',
            header: () => <span>tags</span>,
            cell: (props: { row: any }) => (
                <ActivationTagAssociations
                    activationId={props.row.original.id}
                />
            ),
        }),
        columnHelper.accessor('owner', {
            id: 'owner',
            header: () => <span>owner</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                <ActivationActions onDelete={onDelete} row={props.row} />
            ),
        }),
    ];

    useEffect(() => {
        let isMounted = true;
        const getEvents = async () => {
            try {
                const response = await authenticatedApi.get(
                    `${orgId}/activations/${
                        tagFilters.length > 0
                            ? `?tagFilters=${tagFilters.join()}`
                            : ''
                    }`,
                );
                // @ts-ignore
                setActivations(
                    response.data
                        .filter(
                            (activation: Activation) =>
                                showArchived || !activation.isArchived,
                        )
                        .sort((a: Activation, b: Activation) => {
                            if (a.date > b.date) {
                                return -1;
                            }
                            return 1;
                        }),
                );

                const tags = await getTags();
                setTags(tags);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        getEvents();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderCount, tagFilters, showArchived]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const activeFilterCount =
        (showArchived ? 1 : 0) +
        (dateFrom ? 1 : 0) +
        (dateTo ? 1 : 0) +
        (tagFilters.length > 0 ? 1 : 0);

    const filteredActivations = activations.filter((activation) => {
        if (searchTerms && !activation.name.toLocaleLowerCase().includes(searchTerms.toLocaleLowerCase())) {
            return false;
        }
        if (dateFrom && activation.date.split('T')[0] < dateFrom) {
            return false;
        }
        if (dateTo && activation.date.split('T')[0] > dateTo) {
            return false;
        }
        return true;
    });

    let actions = (
        <>
            <FeatureFlag
                flag={FeatureFlags.ACTIVATION_TEMPLATES_V1}
                fallback={() => (
                    <NavLink to="create">
                        <Button>
                            <Plus className="size-4" />
                            Create Activation
                        </Button>
                    </NavLink>
                )}
            >
                <Button onClick={() => setIsShowingNewActivationModal(true)}>
                    <Plus className="size-4" />
                    Create Activation
                </Button>
            </FeatureFlag>
            <FeatureFlag flag={FeatureFlags.LEGACY_ADMIN_V1}>
                <NavLink to="v3sync">
                    <Button variant="outline">V3 Sync</Button>
                </NavLink>
            </FeatureFlag>
        </>
    );

    return (
        <PageWithTable
            title="Activations"
            actions={actions}
            breadcrumbs={[{ label: 'Activations' }]}
        >
            <div className="flex items-center gap-3 p-4">
                <div className="flex flex-1 max-w-sm items-center gap-2 rounded-md border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                    <Search className="size-4 shrink-0 text-muted-foreground" />
                    <input
                        placeholder="Search activations..."
                        value={searchTerms}
                        onChange={(e) => setSearchTerms(e.target.value)}
                        className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                </div>

                <Popover>
                    <PopoverTrigger className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium whitespace-nowrap hover:bg-accent hover:text-accent-foreground outline-none cursor-pointer">
                        <Filter className="size-4" />
                        Filters
                        {activeFilterCount > 0 && (
                            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                                {activeFilterCount}
                            </Badge>
                        )}
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="end">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <h4 className="text-sm font-medium text-foreground">
                                    Filters
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                    Narrow down your activations list.
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="show-archived"
                                    checked={showArchived}
                                    onCheckedChange={(checked) =>
                                        setShowArchived(checked === true)
                                    }
                                />
                                <Label
                                    htmlFor="show-archived"
                                    className="text-sm font-normal cursor-pointer"
                                >
                                    Include Archived
                                </Label>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    Date From
                                </Label>
                                <Input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="h-8"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    Date To
                                </Label>
                                <Input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="h-8"
                                />
                            </div>

                            <div className="space-y-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => setShowTagFilters(true)}
                                >
                                    <Filter className="size-3.5 mr-1.5" />
                                    Filter by Tags
                                    {tagFilters.length > 0 && (
                                        <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-xs">
                                            {tagFilters.length}
                                        </Badge>
                                    )}
                                </Button>
                            </div>

                            {activeFilterCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-muted-foreground"
                                    onClick={() => {
                                        setShowArchived(false);
                                        setDateFrom('');
                                        setDateTo('');
                                        setTagFilters([]);
                                    }}
                                >
                                    <X className="size-3.5 mr-1.5" />
                                    Clear All Filters
                                </Button>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            <div className="px-4">
            <ActivationsTable
                onDelete={onDelete}
                onClone={onClone}
                key={`activationTable${activations.length}${showArchived}${dateFrom}${dateTo}`}
                data={filteredActivations}
                columns={columnDefinitions}
                enablePagination={true}
            />
            </div>

            {showTagFilters && (
                <ListPickerModal
                    list={tags}
                    selectedIds={tagFilters}
                    onSave={(filters: number[]) => {
                        setTagFilters(filters);
                        setShowTagFilters(false);
                    }}
                    onClose={() => setShowTagFilters(false)}
                />
            )}
            {showingCloneModal && activationToClone ? (
                <CloneActivationModal
                    id={activationToClone?.id}
                    activation={activationToClone}
                    onDismiss={(activationId?: number) => {
                        setShowingCloneModal(false);
                        if (!activationId) return;
                        navigation(`/activations/${activationId}`);
                    }}
                />
            ) : null}
            {isShowingNewActivationModal && (
                <CreateActivationModal
                    onDismiss={() => {
                        setIsShowingNewActivationModal(false);
                    }}
                />
            )}
        </PageWithTable>
    );
}
